import { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import copy from 'copy-to-clipboard';
import { useAppDispatch } from '@store';
import { PermissionEnum, ResponseError, responseErrorsResolver } from '@ui/src';
import {
  BaseInfoFormValues,
  BaseUserInformation,
  GeneralInfo,
  GeneralUserInfo,
} from '../../types';
import {
  useResetCustomerPasswordMutation,
  useLazyGetCustomersChangeLogsQuery,
  useReopenOrganizationMutation,
  useLazyGetCustomerCommentsQuery,
  useAddCommentToCustomerMutation,
  useEditCommentInCustomerMutation,
  useDeleteCommentInCustomerMutation,
} from '../../../customers/store/services/customers';
import {
  useUpdateIndividualUserBaseInformationMutation,
  useConvertCofToIndividualMutation,
} from '../../store/services/individual';
import { OrganizationType } from '../../../common/types';
import { setConvertedToIndividual } from '../../store/slices/general-info';

const useOverview = ({
  customerId,
  baseInformation,
  activePermissions,
  baseUserInformation,
}: {
  customerId?: string;
  baseInformation?: Partial<GeneralInfo>;
  activePermissions?: PermissionEnum[];
  baseUserInformation?: Partial<GeneralUserInfo> & BaseUserInformation;
}) => {
  const [topUpPanelIsOpen, setTopUpPanelIsOpen] = useState(false);
  const [selectedTopUpId, setSelectedTopUpId] = useState('');
  const [token, setToken] = useState('');
  const [resetPasswordPopupVisible, setResetPasswordPopupVisible] =
    useState(false);
  const [copyIsSuccess, setCopyIsSuccess] = useState(false);
  const [globalError, setGlobalError] = useState('');
  const [convertPopupVisible, setConvertPopuVisible] = useState(false);
  const [changeLogsPanelIsOpen, setChangeLogPanelIsOpen] = useState(false);
  const [reopenPopupVisible, setReopenPopupVisible] = useState(false);
  const [reopenPopupState, setReopenPopupState] = useState<
    'warning' | 'comment'
  >('warning');
  const [reopenReason, setReopenReason] = useState('');
  const [reopenError, setReopenError] = useState('');

  const dispatch = useAppDispatch();

  const {
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<BaseInfoFormValues>();

  const [resetCustomerPassword, { isLoading: recoveryInProgress }] =
    useResetCustomerPasswordMutation();

  const [
    reopenOrganization,
    { isLoading: reopenInProgress, isSuccess: reopenIsSuccess },
  ] = useReopenOrganizationMutation();

  const [
    getChangeLogs,
    { isLoading: changeLogsIsLoading, isFetching: changeLogsIsFetching },
  ] = useLazyGetCustomersChangeLogsQuery();

  const [
    getCustomerComments,
    { isLoading: commentsIsLoading, isFetching: commentsIsFetching },
  ] = useLazyGetCustomerCommentsQuery();
  const [
    addCommentToCustomer,
    { isLoading: addCommentInProgress, isSuccess: addCommentIsSuccess },
  ] = useAddCommentToCustomerMutation();
  const [
    editCommentInCustomer,
    { isLoading: editCommentInProgress, isSuccess: editCommentIsSuccess },
  ] = useEditCommentInCustomerMutation();
  const [
    deleteCommentInCustomer,
    { isLoading: deleteCommentInProgress, isSuccess: deleteCommentIsSuccess },
  ] = useDeleteCommentInCustomerMutation();

  const [update, { isLoading, isSuccess }] =
    useUpdateIndividualUserBaseInformationMutation();
  const [
    convertToIndividaul,
    { isLoading: convertIsLoading, isSuccess: convertIsSuccess },
  ] = useConvertCofToIndividualMutation();

  const onReopenOrganization = async () => {
    setReopenError('');

    const response = (await reopenOrganization({
      id: customerId as string,
      body: {
        statusChangeReason: reopenReason,
      },
    })) as { error: ResponseError };

    if (response.error) {
      responseErrorsResolver({
        errors: response.error,
        setError: () => {},
        setGlobalError: setReopenError,
      });
    }
  };

  const onInfoSubmit = handleSubmit(async (values) => {
    setGlobalError('');

    const body: any = {
      type: baseInformation?.type as OrganizationType,
      firstName: values.firstName,
      lastName: values.lastName,
      countryId: values.countryId,
      city: values.city,
      address1: values.address1,
      address2: values.address2,
      postalCode: values.postalCode,
      sourceOfFundsIds: values.sourceOfFundsIds,
      productIds: values.productIds,
      nationalityId: values.nationalityId,
    };

    if (values?.clientActivityId) {
      body.clientActivityId = values.clientActivityId;
    }

    if (values.idExpDate) {
      body.idExpDate = values.idExpDate;
    }

    if (values.idType) {
      body.idType = values.idType;
    }

    if (values.idNumber) {
      body.idNumber = values.idNumber;
    }

    if (values.email) {
      body.email = values.email;
    }

    if (values.dateOfBirth) {
      body.dob = values.dateOfBirth;
    }

    if (values.taxNumber) {
      body.taxNumber = values.taxNumber;
    }

    const response = (await update({
      id: customerId as string,
      body,
    })) as {
      error: ResponseError;
    };

    if (response.error) {
      responseErrorsResolver({
        setError,
        errors: response.error,
        setGlobalError,
      });
    }
  });

  const getChangeLogsAction = useCallback(
    (cursor?: string) => {
      getChangeLogs(
        `?take=20&entityType=ORGANIZATION&entityId=${customerId}${
          cursor ? `&cursor=${cursor}` : ''
        }`,
      );
    },
    [customerId],
  );

  let link: string;

  if (process.env.CUSTOMERS_HOST) {
    link = process.env.CUSTOMERS_HOST;
  } else {
    link = window.location.host.includes('baseella')
      ? window.location.host.replace('backoffice', 'customers')
      : '';
  }

  const onCopy = () => {
    copy(`${link}/auth/reset-password?token=${token}`);

    setCopyIsSuccess(true);

    setTimeout(() => {
      setCopyIsSuccess(false);
    }, 2000);
  };

  const onResetPassword = async () => {
    const response = (await resetCustomerPassword(
      baseInformation?.userId as string,
    )) as {
      data: { data: { token: string } };
    };

    if (response && response.data && response.data.data) {
      setToken(response.data.data.token);
      setResetPasswordPopupVisible(true);
    }
  };

  const onAddCommentToCustomer = async (text: string) => {
    await addCommentToCustomer({
      id: customerId as string,
      body: {
        text,
      },
    });
  };

  const onEditCommentInCustomer = async ({
    text,
    commentId,
  }: {
    text: string;
    commentId: string;
  }) => {
    await editCommentInCustomer({
      id: customerId as string,
      commentId,
      body: {
        text,
      },
    });
  };

  const onDeleteCommentInCustomer = async (commentId: string) => {
    await deleteCommentInCustomer({
      id: customerId as string,
      commentId,
    });
  };

  const getCustomerCommentsAction = async (cursor?: string) => {
    await getCustomerComments({
      id: customerId as string,
      cursor: cursor || '',
    });
  };

  useEffect(() => {
    if (
      activePermissions?.includes(
        PermissionEnum.INDIVIDUAL_CUSTOMERS_COMMENTS_LIST,
      )
    ) {
      getCustomerCommentsAction();
    }
  }, []);

  useEffect(() => {
    if (baseUserInformation) {
      reset({
        type: OrganizationType.INDIVIDUAL,
        firstName: baseUserInformation.firstName,
        lastName: baseUserInformation.lastName,
        employmentStatus: baseUserInformation.employmentStatus,
        countryId: baseUserInformation.countryId,
        city: baseUserInformation.city,
        address1: baseUserInformation.address1,
        address2: baseUserInformation.address2,
        postalCode: baseUserInformation.postalCode,
        sourceOfFundsIds: baseUserInformation.sourceOfFunds,
        dateOfBirth: baseUserInformation.dateOfBirth,
        phone: baseUserInformation.phone,
        email: baseUserInformation.email,
        idType: baseInformation?.idType,
        idNumber: baseInformation?.idNumber,
        idExpDate: baseInformation?.idExpDate,
        productIds: baseUserInformation?.products,
        clientActivityId: baseUserInformation?.clientActivityId,
        nationalityId: baseInformation?.nationalityId,
        taxNumber: baseInformation?.taxNumber || '',
      });
    }
  }, [reset, baseUserInformation]);

  useEffect(() => {
    if (convertIsSuccess) {
      dispatch(setConvertedToIndividual(true));
    }
  }, [convertIsSuccess]);

  useEffect(() => {
    if (
      changeLogsPanelIsOpen &&
      activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST)
    ) {
      getChangeLogsAction();
    }
  }, [changeLogsPanelIsOpen]);

  useEffect(() => {
    if (reopenIsSuccess) {
      setReopenError('');
      setReopenPopupState('warning');
      setReopenPopupVisible(false);
    }
  }, [reopenIsSuccess]);

  return {
    setChangeLogPanelIsOpen,
    setReopenPopupVisible,
    control,
    onInfoSubmit,
    isLoading,
    isSuccess,
    onResetPassword,
    recoveryInProgress,
    globalError,
    errors,
    setConvertPopuVisible,
    commentsIsLoading,
    commentsIsFetching,
    getCustomerCommentsAction,
    setTopUpPanelIsOpen,
    setSelectedTopUpId,

    topUpPanelIsOpen,
    selectedTopUpId,
    onAddCommentToCustomer,
    addCommentInProgress,
    addCommentIsSuccess,
    onEditCommentInCustomer,
    editCommentInProgress,
    editCommentIsSuccess,
    onDeleteCommentInCustomer,
    deleteCommentInProgress,
    deleteCommentIsSuccess,
    convertIsLoading,
    convertPopupVisible,
    convertToIndividaul,
    reopenPopupState,
    setReopenPopupState,
    setReopenError,
    reopenInProgress,
    reopenPopupVisible,
    reopenReason,
    onReopenOrganization,
    reopenError,
    setReopenReason,
    setResetPasswordPopupVisible,
    resetPasswordPopupVisible,
    copyIsSuccess,
    token,
    link,
    onCopy,
    changeLogsPanelIsOpen,
    changeLogsIsLoading,
    changeLogsIsFetching,
    getChangeLogsAction,
  };
};

export default useOverview;
